import { useId } from 'react';

// PMS Loading Spinner — six rotating arcs, one per noyau colour, ported from the
// design system (`Loading Spinner.html`). The arcs and their chord gradients live
// in the viewBox 0 0 100 100; size + revolution speed come from the CSS class
// (.pms-spinner / --xs|--sm|--md|--lg). Honours prefers-reduced-motion via CSS.

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';

interface SpinnerProps {
  size?: SpinnerSize;
  /** Accessible label announced by screen readers. */
  label?: string;
}

// [arc path, colour] pairs in canonical noyau order (bleu-ciel → bleu-nuit),
// matching the design-system mark. Each gradient fades the trailing end into the gap.
const ARCS: { d: string; color: string; x1: number; y1: number; x2: number; y2: number }[] = [
  {
    d: 'M 30.71 10.45 A 44 44 0 0 1 69.29 10.45',
    color: '#009FE3',
    x1: 30.71,
    y1: 10.45,
    x2: 69.29,
    y2: 10.45,
  },
  {
    d: 'M 74.60 13.52 A 44 44 0 0 1 93.89 46.93',
    color: '#009640',
    x1: 74.6,
    y1: 13.52,
    x2: 93.89,
    y2: 46.93,
  },
  {
    d: 'M 93.89 53.07 A 44 44 0 0 1 74.60 86.48',
    color: '#EFD500',
    x1: 93.89,
    y1: 53.07,
    x2: 74.6,
    y2: 86.48,
  },
  {
    d: 'M 69.29 89.55 A 44 44 0 0 1 30.71 89.55',
    color: '#E30613',
    x1: 69.29,
    y1: 89.55,
    x2: 30.71,
    y2: 89.55,
  },
  {
    d: 'M 25.40 86.48 A 44 44 0 0 1 6.11 53.07',
    color: '#E6007E',
    x1: 25.4,
    y1: 86.48,
    x2: 6.11,
    y2: 53.07,
  },
  {
    d: 'M 6.11 46.93 A 44 44 0 0 1 25.40 13.52',
    color: '#312783',
    x1: 6.11,
    y1: 46.93,
    x2: 25.4,
    y2: 13.52,
  },
];

// Trailing-fade opacity stops shared by every arc gradient.
const STOPS: { offset: string; opacity: number }[] = [
  { offset: '0%', opacity: 1 },
  { offset: '30%', opacity: 1 },
  { offset: '50%', opacity: 0.58 },
  { offset: '65%', opacity: 0.25 },
  { offset: '80%', opacity: 0.07 },
  { offset: '100%', opacity: 0 },
];

export function Spinner({ size = 'md', label = 'Chargement' }: SpinnerProps) {
  // Namespace gradient ids so multiple spinners on the page never collide.
  const uid = useId().replace(/:/g, '');
  return (
    <svg
      className={`pms-spinner pms-spinner--${size}`}
      viewBox="0 0 100 100"
      role="img"
      aria-label={label}
    >
      <defs>
        {ARCS.map((arc, i) => (
          <linearGradient
            key={arc.color}
            id={`pms-grad-${uid}-${i}`}
            gradientUnits="userSpaceOnUse"
            x1={arc.x1}
            y1={arc.y1}
            x2={arc.x2}
            y2={arc.y2}
          >
            {STOPS.map((stop) => (
              <stop
                key={stop.offset}
                offset={stop.offset}
                stopColor={arc.color}
                stopOpacity={stop.opacity}
              />
            ))}
          </linearGradient>
        ))}
      </defs>
      {ARCS.map((arc, i) => (
        <path key={arc.color} d={arc.d} stroke={`url(#pms-grad-${uid}-${i})`} />
      ))}
    </svg>
  );
}
